import { PTextFieldWrapper, componentsReady } from '@porsche-design-system/components-react';
import { type InputProps as AntdInputProps } from 'antd';
import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const StyledTextFieldWrapper = styled(PTextFieldWrapper)`
    width: 100%;
`;

const getTextFieldState = (status: AntdInputProps['status'] & 'success') => {
    switch (status) {
        case 'warning':
            return 'none';
        case 'error':
            return 'error';
        case 'success':
            return 'success';
        default:
            return 'none';
    }
};

const ensureSuffix = (suffix: AntdInputProps['suffix']) => {
    if (typeof suffix === 'string') {
        return '   ';
    }

    return undefined;
};

/**
 * note: the max character length of the suffix props is 5,
 * hence the use of '   ' to ensure the suffix is displayed correctly
 * and this hook is a workaround to display suffix (string only) with more than 5 characters
 * ref: https://designsystem.porsche.com/v3/components/text-field-wrapper/examples#type-number
 */
const useEnsureSuffix = (wrapperRef: React.RefObject<HTMLDivElement>, suffix: AntdInputProps['suffix']) => {
    const hasSetSuffix = useRef(false);

    useEffect(() => {
        if (!suffix || typeof suffix !== 'string' || hasSetSuffix.current) {
            return;
        }

        const wrapper = wrapperRef.current;

        componentsReady().then(() => {
            const shadowRoot = wrapper?.shadowRoot;
            const suffixSpan = shadowRoot?.querySelector('.unit-counter');

            if (!suffixSpan) {
                return;
            }

            suffixSpan.innerHTML = suffix;
            hasSetSuffix.current = true;
        });
    }, [suffix, wrapperRef]);
};

const Input = ({
    type = 'text',
    placeholder,
    onChange,
    status,
    disabled,
    value,
    onBlur,
    onFocus,
    onPressEnter,
    onKeyPress,
    suffix,
}: AntdInputProps & { status?: 'success' }) => {
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEnsureSuffix(wrapperRef, suffix);

    return (
        <StyledTextFieldWrapper
            ref={wrapperRef}
            className="porsche-v3-calculator-input-field-wrapper"
            state={getTextFieldState(status)}
            unit={ensureSuffix(suffix)}
            unitPosition={suffix ? 'suffix' : undefined}
        >
            <input
                disabled={disabled}
                onBlur={onBlur}
                onChange={onChange}
                onFocus={onFocus}
                onKeyDown={e => {
                    if (e.key === 'Enter' && onPressEnter) {
                        onPressEnter(e);
                    }
                }}
                onKeyPress={onKeyPress}
                placeholder={placeholder}
                type={type}
                value={value}
            />
        </StyledTextFieldWrapper>
    );
};

export default Input;
